import { useState } from 'react';
import { App, Button, Flex, Input, Typography } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { useBreakpoint } from '@/shared/lib/hooks';
import { LinesGrid, useLinesGridApi } from '@/shared/ui/LinesGrid';
import { EditorFooter, useEditorWindow } from '@/shared/ui';
import type { LinesGridColumn } from '@/shared/ui/LinesGrid';
import { useCreateCounterpartyMutation } from '@/entities/counterparty';
import { translateApiError } from '@/shared/api/errorToMessage';

export interface CounterpartyCreateWindowProps {
  onClose: () => void;
}

interface Row {
  key: string;
  name: string;
  phone: string;
}

let keySeq = 0;
function emptyRow(): Row {
  keySeq += 1;
  return { key: `cp${keySeq}-${Date.now()}`, name: '', phone: '' };
}

/** §2.2: добавление контрагентов — плавающее окно (двигается/растягивается, как окна документов)
 *  с табличной формой вместо одиночной antd-модалки, позволяет ввести сразу несколько строк и
 *  сохранить всех разом; строка снизу добавляется автоматически по заполнению «Название»
 *  (тот же приём, что и автодобавление строки товара в документе). */
export function CounterpartyCreateWindow({ onClose }: CounterpartyCreateWindowProps) {
  const { message } = App.useApp();
  const { isMobile } = useBreakpoint();
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [createCounterparty, { isLoading }] = useCreateCounterpartyMutation();

  const { gridApiRef, api: gridApi } = useLinesGridApi();

  /** Пустая строка снизу и так ждёт ввода — новую не плодим, а встаём на неё. */
  function addRow(): boolean {
    const last = rows[rows.length - 1];
    if (last && !last.name && !last.phone) {
      gridApi.focusCell(rows.length - 1, 0);
      return false;
    }
    setRows((prev) => [...prev, emptyRow()]);
    return true;
  }

  function removeRow(key: string) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  }

  function updateName(key: string, rowIndex: number, value: string) {
    setRows((prev) => {
      const wasEmpty = !prev[rowIndex]?.name;
      const next = prev.map((r) => (r.key === key ? { ...r, name: value } : r));
      if (rowIndex === prev.length - 1 && wasEmpty && value.trim()) {
        return [...next, emptyRow()];
      }
      return next;
    });
  }

  function updatePhone(key: string, value: string) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, phone: value } : r)));
  }

  const filled = rows.filter((r) => r.name.trim());

  async function handleSave() {
    if (filled.length === 0 || isLoading) return;
    try {
      await Promise.all(
        filled.map((r) =>
          createCounterparty({ name: r.name.trim(), phone: r.phone.trim() || undefined, isSupplier: true, isCustomer: true }).unwrap(),
        ),
      );
      message.success(filled.length > 1 ? `Добавлено контрагентов: ${filled.length}` : 'Контрагент добавлен');
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  const columns: LinesGridColumn<Row>[] = [
    {
      key: 'name',
      title: 'Название',
      width: 260,
      render: (row, rowIndex) => (
        <Input
          className="nav-cell"
          data-nav-row={rowIndex}
          data-nav-col={0}
          value={row.name}
          autoFocus={rowIndex === 0}
          onChange={(e) => updateName(row.key, rowIndex, e.target.value)}
        />
      ),
    },
    {
      key: 'phone',
      title: 'Телефон',
      width: 180,
      render: (row, rowIndex) => (
        <Input
          className="nav-cell"
          data-nav-row={rowIndex}
          data-nav-col={1}
          value={row.phone}
          onChange={(e) => updatePhone(row.key, e.target.value)}
        />
      ),
    },
  ];

  const editor = useEditorWindow({
    dirty: rows.some((r) => r.name.trim() || r.phone.trim()),
    saveLabel: `Сохранить${filled.length > 1 ? ` (${filled.length})` : ''}`,
    saveBlockReason: filled.length === 0 ? 'Введите название хотя бы одного контрагента' : null,
    saving: isLoading,
    onSave: handleSave,
    onClose,
    gridApi,
  });

  return (
    <FloatingWindow
      kind="counterpartyCreate"
      title="Новые контрагенты"
      onClose={editor.requestClose}
      hotkeys={editor.bindings}
      // На телефоне курсор при открытии не ставим: он сразу поднял бы экранную клавиатуру.
      initialFocus={isMobile ? 'window' : undefined}
      footer={
        isMobile ? (
          // Телефон: «Сохранить» на всю ширину, «Закрыть» рядом; причина недоступности — текстом.
          <Flex vertical gap={6}>
            {editor.save.disabledReason ? (
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {editor.save.disabledReason}
              </Typography.Text>
            ) : null}
            <Flex gap={8}>
              <Button type="primary" size="large" loading={isLoading} disabled={editor.save.disabled} onClick={editor.save.run} style={{ flex: 1, minWidth: 0 }}>
                {editor.save.label}
              </Button>
              <Button size="large" onClick={editor.requestClose}>
                Закрыть
              </Button>
            </Flex>
          </Flex>
        ) : (
          <EditorFooter save={editor.save} saving={isLoading} onRequestClose={editor.requestClose} />
        )
      }
    >
      {editor.dialog}
      <Flex vertical gap={isMobile ? 12 : 8}>
        <Typography.Text type="secondary" style={{ fontSize: isMobile ? 13 : 12 }}>
          Заполните название — строка снизу добавится автоматически. Пустые строки не сохраняются.
        </Typography.Text>
        {isMobile ? (
          // Телефон: вместо таблицы — вертикальная форма, по карточке на контрагента.
          <>
            <div>
              {rows.map((row, rowIndex) => (
                <div key={row.key} className="m-card">
                  <Flex justify="space-between" align="center" gap={8}>
                    <Typography.Text type="secondary">Контрагент {rowIndex + 1}</Typography.Text>
                    {rows.length > 1 ? (
                      <Button type="text" danger aria-label="Удалить строку" icon={<CloseOutlined />} style={{ width: 44, height: 44 }} onClick={() => removeRow(row.key)} />
                    ) : null}
                  </Flex>
                  <Flex vertical gap={4} style={{ marginBottom: 8 }}>
                    <label htmlFor={`cp-name-${row.key}`}>Название</label>
                    <Input
                      id={`cp-name-${row.key}`}
                      value={row.name}
                      autoComplete="off"
                      autoCapitalize="sentences"
                      enterKeyHint="next"
                      onChange={(e) => updateName(row.key, rowIndex, e.target.value)}
                    />
                  </Flex>
                  <Flex vertical gap={4}>
                    <label htmlFor={`cp-phone-${row.key}`}>Телефон</label>
                    <Input
                      id={`cp-phone-${row.key}`}
                      value={row.phone}
                      type="tel"
                      inputMode="tel"
                      autoComplete="off"
                      onChange={(e) => updatePhone(row.key, e.target.value)}
                    />
                  </Flex>
                </div>
              ))}
            </div>
            <Button size="large" icon={<PlusOutlined />} onClick={() => void addRow()}>
              Добавить ещё
            </Button>
          </>
        ) : (
          <LinesGrid lines={rows} columns={columns} getRowKey={(r) => r.key} onAddRow={addRow} onRemoveRow={removeRow} gridApiRef={gridApiRef} removeRowAction={editor.removeRow} />
        )}
      </Flex>
    </FloatingWindow>
  );
}
